import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createReservation, fetchReservationStatus, formatRWF, isRwandanMobile } from '../api';
import { useI18n } from '../i18n/LanguageContext';

// Used only if the pilgrimage came without departures (e.g. showcase data).
const FALLBACK_DEPARTURES = [
  { id: '', name: 'Sainte Famille', departureTime: '05:00' },
];

const MAX_PEOPLE = 20;
const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 4 * 60 * 1000;

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6l-12 12" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function MinusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M5 12h14" />
    </svg>
  );
}
function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12l5 5 9-11" />
    </svg>
  );
}
function PinSmallIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function PickupDropdown({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = options.find((o) => o.id === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div className={`modal-dropdown ${open ? 'is-open' : ''}`} ref={ref}>
      <button
        type="button"
        className="modal-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="modal-dropdown-value">
          <PinSmallIcon />
          {current?.name}
        </span>
        <span className="modal-dropdown-caret"><ChevronDownIcon /></span>
      </button>
      {open && (
        <ul className="modal-dropdown-menu" role="listbox">
          {options.map((opt) => {
            const active = opt.id === current?.id;
            return (
              <li
                key={opt.id || opt.name}
                role="option"
                aria-selected={active}
                className={`modal-dropdown-option ${active ? 'is-active' : ''}`}
                onClick={() => { onChange(opt.id); setOpen(false); }}
              >
                <span className="modal-dropdown-option-label">
                  <PinSmallIcon />
                  {opt.name}
                  {opt.departureTime && <span className="modal-dropdown-option-time">{opt.departureTime}</span>}
                </span>
                {active && <span className="modal-dropdown-option-check"><CheckIcon /></span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function GenderToggle({ value, onChange, name }) {
  const { t } = useI18n();
  return (
    <div className="rsv-segment" role="radiogroup" aria-label={t('booking.gender')}>
      {[['FEMALE', t('booking.female')], ['MALE', t('booking.male')]].map(([v, label]) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={value === v}
          name={name}
          className={`rsv-segment-btn ${value === v ? 'is-active' : ''}`}
          onClick={() => onChange(value === v ? '' : v)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function MomoBadge() {
  return (
    <span className="modal-pay-momo">
      <span className="modal-pay-momo-dot" />
      MoMo
    </span>
  );
}
function CardBadges() {
  return (
    <span className="rsv-card-badges">
      <span className="modal-pay-visa">VISA</span>
      <span className="modal-pay-mc" aria-label="Mastercard">
        <span className="modal-pay-mc-a" />
        <span className="modal-pay-mc-b" />
      </span>
    </span>
  );
}

const emptyTraveller = (defaults = {}) => ({
  firstName: '',
  lastName: '',
  phone: '',
  parish: defaults.parish || '',
  gender: '',
  departureLocationId: defaults.departureLocationId || '',
  communityMember: false,
});

/** "Member of the Indabo za Mariya community" tick box. */
function MemberCheck({ checked, onChange, label }) {
  return (
    <label className={`rsv-member ${checked ? 'is-checked' : ''}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="rsv-member-box" aria-hidden="true"><CheckIcon /></span>
      <span className="rsv-member-label">{label}</span>
    </label>
  );
}

function travellerComplete(t) {
  return Boolean(t.firstName.trim() && t.lastName.trim() && (!t.phone.trim() || isRwandanMobile(t.phone)));
}

/**
 * Additional traveller, rendered as an accordion row: the most recently
 * added one is open, earlier ones collapse to a one-line summary.
 */
function TravellerCard({ index, traveller, open, onToggle, onChange, onRemove, departures, showErrors }) {
  const { t } = useI18n();
  const complete = travellerComplete(traveller);
  const location = departures.find((d) => d.id === traveller.departureLocationId) || departures[0];
  const set = (field) => (e) => onChange({ ...traveller, [field]: e?.target ? e.target.value : e });
  const nameError = showErrors && (!traveller.firstName.trim() || !traveller.lastName.trim());
  const phoneError = traveller.phone.trim() && !isRwandanMobile(traveller.phone);

  return (
    <div className={`rsv-traveller ${open ? 'is-open' : ''} ${showErrors && !complete ? 'has-error' : ''}`}>
      <button type="button" className="rsv-traveller-head" onClick={onToggle} aria-expanded={open}>
        <span className={`rsv-traveller-num ${complete ? 'is-done' : ''}`}>
          {complete && !open ? <CheckIcon /> : index + 1}
        </span>
        <span className="rsv-traveller-summary">
          <span className="rsv-traveller-name">
            {traveller.firstName || traveller.lastName
              ? `${traveller.firstName} ${traveller.lastName}`.trim()
              : t('booking.traveller_n', { n: index + 1 })}
          </span>
          {!open && (
            <span className="rsv-traveller-sub">
              {complete ? [traveller.parish, location?.name].filter(Boolean).join(' · ') || t('booking.details_added') : t('booking.details_needed')}
            </span>
          )}
        </span>
        <span className="rsv-traveller-caret"><ChevronDownIcon /></span>
      </button>

      {open && (
        <div className="rsv-traveller-body">
          <div className="modal-form-row">
            <div className="modal-field">
              <label className="modal-label">{t('booking.first_name')}</label>
              <input className={`modal-input ${nameError && !traveller.firstName.trim() ? 'is-invalid' : ''}`} value={traveller.firstName} onChange={set('firstName')} placeholder={t('booking.first_name_short_ph')} autoFocus />
            </div>
            <div className="modal-field">
              <label className="modal-label">{t('booking.last_name')}</label>
              <input className={`modal-input ${nameError && !traveller.lastName.trim() ? 'is-invalid' : ''}`} value={traveller.lastName} onChange={set('lastName')} placeholder={t('booking.last_name_short_ph')} />
            </div>
          </div>
          <div className="modal-form-row">
            <div className="modal-field">
              <label className="modal-label">{t('booking.phone')} <span className="rsv-optional">{t('booking.optional')}</span></label>
              <input className={`modal-input ${phoneError ? 'is-invalid' : ''}`} type="tel" value={traveller.phone} onChange={set('phone')} placeholder="07XX XXX XXX" />
            </div>
            <div className="modal-field">
              <label className="modal-label">{t('booking.parish')}</label>
              <input className="modal-input" value={traveller.parish} onChange={set('parish')} placeholder={t('booking.parish_ph')} />
            </div>
          </div>
          <div className="modal-form-row">
            <div className="modal-field">
              <label className="modal-label">{t('booking.gender')}</label>
              <GenderToggle value={traveller.gender} onChange={set('gender')} name={`gender-${index}`} />
            </div>
            <div className="modal-field">
              <label className="modal-label">{t('booking.pickup')}</label>
              <PickupDropdown value={traveller.departureLocationId} onChange={set('departureLocationId')} options={departures} />
            </div>
          </div>
          <MemberCheck
            checked={traveller.communityMember}
            onChange={(v) => onChange({ ...traveller, communityMember: v })}
            label={t('booking.member_other')}
          />
          <div className="rsv-traveller-actions">
            <button type="button" className="rsv-link-btn rsv-link-btn--danger" onClick={onRemove}>
              <TrashIcon /> {t('booking.remove_traveller')}
            </button>
            {complete && (
              <button type="button" className="rsv-link-btn" onClick={onToggle}>{t('booking.done')}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PaymentStatusView({ reservation, error, onRetry, onClose }) {
  const { t } = useI18n();
  const status = reservation?.status;
  const people = (n) => t(n === 1 ? 'booking.people_one' : 'booking.people_other', { n });
  if (status === 'CONFIRMED') {
    return (
      <div className="rsv-status rsv-status--ok">
        <div className="rsv-status-icon"><CheckIcon /></div>
        <h3 className="rsv-status-title">{t('booking.reserved_title')}</h3>
        <p className="rsv-status-text">
          {t('booking.reserved_text', {
            amount: <strong>{formatRWF(reservation.totalAmount)}</strong>,
            people: people(reservation.peopleCount),
            event: reservation.eventName,
          })}
        </p>
        <div className="rsv-status-ref">{t('booking.reference')} <strong>{reservation.code}</strong></div>
        <p className="rsv-status-hint">{t('booking.reference_hint')}</p>
        {onClose && <button type="button" className="modal-btn modal-btn--primary" onClick={onClose}>{t('booking.done')}</button>}
      </div>
    );
  }
  if (status === 'FAILED' || status === 'CANCELLED' || error) {
    return (
      <div className="rsv-status rsv-status--fail">
        <div className="rsv-status-icon"><CloseIcon /></div>
        <h3 className="rsv-status-title">{t('booking.failed_title')}</h3>
        <p className="rsv-status-text">
          {(error && t(error)) || reservation?.message || t('booking.failed_text')}
        </p>
        <div className="rsv-status-actions">
          {onClose && <button type="button" className="modal-btn modal-btn--ghost" onClick={onClose}>{t('booking.close')}</button>}
          {onRetry && <button type="button" className="modal-btn modal-btn--primary" onClick={onRetry}>{t('booking.try_again')}</button>}
        </div>
      </div>
    );
  }
  return (
    <div className="rsv-status">
      <div className="rsv-spinner" aria-hidden="true" />
      <h3 className="rsv-status-title">
        {reservation?.payMethod === 'CARD' ? t('booking.confirming_card') : t('booking.approve_phone')}
      </h3>
      {reservation?.payMethod !== 'CARD' && (
        <p className="rsv-status-text">
          {t('booking.momo_sent', {
            amount: <strong>{formatRWF(reservation?.totalAmount)}</strong>,
            ussd: <strong>*182*7*1#</strong>,
          })}
        </p>
      )}
      {reservation?.code && <div className="rsv-status-ref">{t('booking.reference')} <strong>{reservation.code}</strong></div>}
      <p className="rsv-status-hint">{t('booking.auto_update')}</p>
    </div>
  );
}

/** Polls the backend until the reservation leaves PENDING_PAYMENT (or we give up). */
export function useReservationPolling(reference, initial) {
  const [reservation, setReservation] = useState(initial || null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!reference) return undefined;
    let cancelled = false;
    let timer;
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const next = await fetchReservationStatus(reference);
        if (cancelled) return;
        setReservation(next);
        if (next.status !== 'PENDING_PAYMENT') return;
      } catch (e) {
        if (cancelled) return;
        if (e.status === 404) { setError('booking.not_found'); return; }
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setError('booking.timeout');
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };
    timer = setTimeout(tick, 1500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [reference]);

  return { reservation, error };
}

export default function ReservationModal({ pilgrimage, onClose }) {
  const { t, formatDate, formatDeadline } = useI18n();
  const event = pilgrimage.event || {};
  const departures = event.departures && event.departures.length ? event.departures : FALLBACK_DEPARTURES;
  const unitPrice = Number(event.price) || 0;
  const feePerPerson = Number(event.feePerPerson) || 0;
  // No place limit — the cap only keeps a single booking a reasonable size.
  const maxPeople = MAX_PEOPLE;

  const [step, setStep] = useState(1);
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    parish: '',
    gender: '',
    departureLocationId: departures[0].id,
    communityMember: false,
  });
  const [others, setOthers] = useState([]);
  const [openIndex, setOpenIndex] = useState(-1);
  const [showErrors, setShowErrors] = useState(false);
  const [method, setMethod] = useState('MOMO');
  const [momoNumber, setMomoNumber] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [booking, setBooking] = useState(null);
  const bodyRef = useRef(null);

  const { reservation, error: pollError } = useReservationPolling(booking?.reference, booking);

  const blockClose = submitting || (step === 3 && reservation?.status === 'PENDING_PAYMENT' && !pollError);
  const handleClose = useCallback(() => { if (!blockClose) onClose(); }, [blockClose, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [handleClose]);

  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
  }, [step]);

  const people = others.length + 1;
  const transportTotal = unitPrice * people;
  const feeTotal = feePerPerson * people;
  const grand = transportTotal + feeTotal;
  const contactLocation = departures.find((d) => d.id === contact.departureLocationId) || departures[0];

  const setC = (field) => (e) => setContact((c) => ({ ...c, [field]: e?.target ? e.target.value : e }));

  const contactErrors = useMemo(() => ({
    firstName: !contact.firstName.trim(),
    lastName: !contact.lastName.trim(),
    phone: !isRwandanMobile(contact.phone),
    email: Boolean(contact.email.trim()) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim()),
    parish: !contact.parish.trim(),
  }), [contact]);
  const contactValid = !Object.values(contactErrors).some(Boolean);
  const detailsValid = contactValid && others.every(travellerComplete);

  const addTraveller = () => {
    if (people >= maxPeople) return;
    setOthers((list) => [...list, emptyTraveller({ parish: contact.parish, departureLocationId: contact.departureLocationId })]);
    setOpenIndex(others.length);
  };
  const removeTraveller = (index) => {
    setOthers((list) => list.filter((_, i) => i !== index));
    setOpenIndex(-1);
  };
  const removeLast = () => { if (others.length) removeTraveller(others.length - 1); };

  const goToPayment = () => {
    if (!detailsValid) {
      setShowErrors(true);
      const firstBad = others.findIndex((t) => !travellerComplete(t));
      if (contactValid && firstBad >= 0) setOpenIndex(firstBad);
      return;
    }
    if (!momoNumber) setMomoNumber(contact.phone);
    setStep(2);
  };

  const paymentValid = agree && (method === 'CARD' || isRwandanMobile(momoNumber));

  const handleSubmit = async () => {
    if (!paymentValid || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const participants = [
        {
          firstName: contact.firstName,
          lastName: contact.lastName,
          phone: contact.phone,
          parish: contact.parish,
          gender: contact.gender || null,
          communityMember: contact.communityMember,
          departureLocationId: contact.departureLocationId,
        },
        ...others.map((t) => ({
          firstName: t.firstName,
          lastName: t.lastName,
          phone: t.phone || null,
          parish: t.parish || null,
          gender: t.gender || null,
          communityMember: Boolean(t.communityMember),
          departureLocationId: t.departureLocationId || contact.departureLocationId,
        })),
      ];
      const result = await createReservation({
        eventId: event.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        phone: contact.phone,
        email: contact.email || null,
        parish: contact.parish,
        participants,
        payMethod: method,
        momoNumber: method === 'MOMO' ? momoNumber : null,
        acceptTerms: agree,
      });
      if (method === 'CARD' && result.paymentUrl) {
        // Card details are entered on the gateway's secure page, never here.
        window.location.assign(result.paymentUrl);
        return;
      }
      setBooking(result);
      setStep(3);
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setBooking(null);
    setAgree(false);
    setStep(2);
  };

  const invalid = (field) => (showErrors && contactErrors[field] ? 'is-invalid' : '');

  return (
    <div className="modal-overlay" onClick={handleClose} role="dialog" aria-modal="true">
      <div className="modal-panel" onClick={(e) => e.stopPropagation()} ref={bodyRef}>
        <button type="button" className="modal-close" onClick={handleClose} aria-label={t('booking.close')} disabled={blockClose}>
          <CloseIcon />
        </button>

        <div className="modal-head">
          <h2 className="modal-title">{t('booking.title')}</h2>
          <p className="modal-subtitle">
            {step === 1 && t('booking.sub_details')}
            {step === 2 && t('booking.sub_payment')}
            {step === 3 && t('booking.sub_confirm')}
          </p>
        </div>

        <div className="modal-body">
          <div className="modal-steps">
            {[t('booking.step_details'), t('booking.step_payment'), t('booking.step_confirm')].map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="modal-step-line" />}
                <div className={`modal-step ${step === i + 1 ? 'is-active' : step > i + 1 ? 'is-done' : ''}`}>
                  <span className="modal-step-num">{i + 1}</span>
                  <span className="modal-step-label">{label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>

          {step < 3 && (
            <>
              <div className="modal-hero">
                <img src={pilgrimage.image} alt={pilgrimage.title} />
                <span className="modal-hero-price">{formatRWF(unitPrice)}</span>
              </div>

              <div className="modal-spot-head">
                <h3 className="modal-spot-title">{pilgrimage.title}</h3>
                {pilgrimage.closesAt && (
                  <span className="modal-spots">{t('pilgrimages.closes', { when: formatDeadline(pilgrimage.closesAt) })}</span>
                )}
              </div>

              <div className="modal-pill-row">
                <span className="modal-pill">{formatDate(pilgrimage.dateIso)}</span>
                <span className="modal-pill">{pilgrimage.location}</span>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="modal-info-grid">
                <div>
                  <div className="modal-section-title">{t('booking.description')}</div>
                  <p className="modal-desc">{pilgrimage.descriptionKey ? t(pilgrimage.descriptionKey) : pilgrimage.description}</p>
                </div>
                <iframe
                  className="modal-map"
                  title={t('booking.map_title')}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(pilgrimage.location + ', Rwanda')}&output=embed`}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>

              <div className="modal-section-title">{t('booking.important')}</div>
              <div className="modal-pill-row">
                <span className="modal-pill">{t('booking.departure_time', { time: contactLocation?.departureTime || '05:00' })}</span>
                <span className="modal-pill">{t('booking.departure_location', { place: contactLocation?.name })}</span>
              </div>

              <div className="rsv-section">
                <div className="rsv-section-head">
                  <span className="rsv-traveller-num is-lead">1</span>
                  <div>
                    <div className="rsv-section-title">{t('booking.your_details')}</div>
                    <div className="rsv-section-sub">{t('booking.your_details_sub')}</div>
                  </div>
                </div>
                <div className="modal-form">
                  <div className="modal-form-row">
                    <div className="modal-field">
                      <label className="modal-label">{t('booking.first_name')}</label>
                      <input className={`modal-input ${invalid('firstName')}`} value={contact.firstName} onChange={setC('firstName')} placeholder={t('booking.first_name_ph')} />
                    </div>
                    <div className="modal-field">
                      <label className="modal-label">{t('booking.last_name')}</label>
                      <input className={`modal-input ${invalid('lastName')}`} value={contact.lastName} onChange={setC('lastName')} placeholder={t('booking.last_name_ph')} />
                    </div>
                  </div>
                  <div className="modal-form-row">
                    <div className="modal-field">
                      <label className="modal-label">{t('booking.phone_number')}</label>
                      <input className={`modal-input ${invalid('phone')}`} type="tel" value={contact.phone} onChange={setC('phone')} placeholder="07XX XXX XXX" />
                      {showErrors && contactErrors.phone && <span className="rsv-field-error">{t('booking.invalid_phone')}</span>}
                    </div>
                    <div className="modal-field">
                      <label className="modal-label">{t('booking.email')} <span className="rsv-optional">{t('booking.optional')}</span></label>
                      <input className={`modal-input ${invalid('email')}`} type="email" value={contact.email} onChange={setC('email')} placeholder="you@example.com" />
                    </div>
                  </div>
                  <div className="modal-form-row">
                    <div className="modal-field">
                      <label className="modal-label">{t('booking.parish')}</label>
                      <input className={`modal-input ${invalid('parish')}`} value={contact.parish} onChange={setC('parish')} placeholder={t('booking.parish_ph')} />
                    </div>
                    <div className="modal-field">
                      <label className="modal-label">{t('booking.gender')} <span className="rsv-optional">{t('booking.optional')}</span></label>
                      <GenderToggle value={contact.gender} onChange={setC('gender')} name="gender-lead" />
                    </div>
                  </div>
                  <div className="modal-field">
                    <label className="modal-label">{t('booking.pickup')}</label>
                    <PickupDropdown value={contact.departureLocationId} onChange={setC('departureLocationId')} options={departures} />
                  </div>
                  <MemberCheck
                    checked={contact.communityMember}
                    onChange={(v) => setContact((c) => ({ ...c, communityMember: v }))}
                    label={t('booking.member_self')}
                  />
                </div>
              </div>

              <div className="rsv-section">
                <div className="rsv-people-row">
                  <div>
                    <div className="rsv-section-title">{t('booking.who')}</div>
                    <div className="rsv-section-sub">
                      {t('booking.per_person', { price: formatRWF(unitPrice + feePerPerson) })}
                    </div>
                  </div>
                  <div className="modal-people-control">
                    <button type="button" className="modal-people-btn" onClick={removeLast} disabled={people <= 1} aria-label={t('booking.remove_aria')}>
                      <MinusIcon />
                    </button>
                    <span className="modal-people-count">{people}</span>
                    <button type="button" className="modal-people-btn" onClick={addTraveller} disabled={people >= maxPeople} aria-label={t('booking.add_aria')}>
                      <PlusIcon />
                    </button>
                  </div>
                </div>

                {others.length > 0 && (
                  <div className="rsv-travellers">
                    {others.map((t, i) => (
                      <TravellerCard
                        key={i}
                        index={i + 1}
                        traveller={t}
                        open={openIndex === i}
                        onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                        onChange={(next) => setOthers((list) => list.map((x, j) => (j === i ? next : x)))}
                        onRemove={() => removeTraveller(i)}
                        departures={departures}
                        showErrors={showErrors}
                      />
                    ))}
                  </div>
                )}

                <button type="button" className="rsv-add-btn" onClick={addTraveller} disabled={people >= maxPeople}>
                  <PlusIcon /> {t('booking.add_traveller')}
                </button>
              </div>

              {showErrors && !detailsValid && (
                <div className="rsv-alert">{t('booking.complete_fields')}</div>
              )}
            </>
          )}

          {step === 2 && (
            <div className="modal-payment-card">
              <div className="modal-pay-row modal-pay-row--col">
                <span className="modal-pay-label">{t('booking.travellers')}</span>
                <ul className="rsv-recap">
                  {[contact, ...others].map((tr, i) => {
                    const loc = departures.find((d) => d.id === tr.departureLocationId) || contactLocation;
                    return (
                      <li key={i}>
                        <span className="rsv-recap-name">{i + 1}. {tr.firstName} {tr.lastName}</span>
                        <span className="rsv-recap-meta">
                          {tr.communityMember && <span className="rsv-member-tag">{t('booking.member_tag')}</span>}
                          {loc?.name}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="modal-pay-row modal-pay-row--col">
                <span className="modal-pay-label">{t('booking.summary')}</span>
                <div className="modal-charge-list">
                  <div className="modal-charge-line">
                    <span>{t('booking.transport_line', { n: people })}</span>
                    <span>{formatRWF(transportTotal)}</span>
                  </div>
                  <div className="modal-charge-line">
                    <span>{t('booking.fee_line', { n: people })}</span>
                    <span>{formatRWF(feeTotal)}</span>
                  </div>
                </div>
              </div>

              <div className="modal-pay-row modal-pay-row--total">
                <span className="modal-pay-label">{t('booking.total')}</span>
                <span className="modal-charge-total">
                  {formatRWF(grand)} <span className="modal-charge-total-sub">/ {t(people === 1 ? 'booking.people_one' : 'booking.people_other', { n: people })}</span>
                </span>
              </div>

              <div className="modal-pay-row modal-pay-row--col">
                <span className="modal-pay-label">{t('booking.payment_method')}</span>
                <div className="modal-pay-methods rsv-pay-methods">
                  <button type="button" className={`modal-pay-method ${method === 'MOMO' ? 'is-active' : ''}`} onClick={() => setMethod('MOMO')}>
                    <MomoBadge />
                    <span className="rsv-pay-method-label">MTN / Airtel</span>
                  </button>
                  <button type="button" className={`modal-pay-method ${method === 'CARD' ? 'is-active' : ''}`} onClick={() => setMethod('CARD')}>
                    <CardBadges />
                    <span className="rsv-pay-method-label">{t('booking.card')}</span>
                  </button>
                </div>
              </div>

              {method === 'MOMO' ? (
                <div className="modal-pay-fields">
                  <div className="modal-pay-field">
                    <label className="modal-pay-field-label">{t('booking.momo_number')}</label>
                    <input
                      className="modal-pay-input"
                      type="tel"
                      value={momoNumber}
                      onChange={(e) => setMomoNumber(e.target.value)}
                      placeholder="07XX XXX XXX"
                    />
                  </div>
                  <p className="rsv-pay-note">{t('booking.momo_note', { amount: formatRWF(grand) })}</p>
                </div>
              ) : (
                <p className="rsv-pay-note rsv-pay-note--card">
                  <LockIcon /> {t('booking.card_note')}
                </p>
              )}

              <label className="modal-agree">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                {t('booking.agree')}
              </label>

              {submitError && <div className="rsv-alert">{submitError}</div>}
            </div>
          )}

          {step === 3 && (
            <PaymentStatusView
              reservation={reservation || booking}
              error={pollError}
              onRetry={retry}
              onClose={onClose}
            />
          )}

          {step < 3 && (
            <div className="modal-actions">
              {step === 2 && (
                <button type="button" className="modal-btn modal-btn--ghost" onClick={() => setStep(1)} disabled={submitting}>
                  {t('booking.back')}
                </button>
              )}
              {step === 1 ? (
                <button type="button" className="modal-btn modal-btn--primary" onClick={goToPayment}>
                  {t('booking.next')}
                </button>
              ) : (
                <button type="button" className="modal-btn modal-btn--primary" onClick={handleSubmit} disabled={!paymentValid || submitting}>
                  {submitting ? t('booking.processing') : t('booking.pay', { amount: formatRWF(grand) })}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
